import { action, observable, makeObservable } from 'mobx';
import { SelectInputComponent, SelectInputState } from '../../components/input/input.select';
import { IkeInputStateGroup } from '../../components/input/input.state.group';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { DATE_OPTIONS, UNIT_OPTIONS } from '../../services/user.settings.service';

const CSS = {
	error: 'c-Settings__error',
	saved: 'c-Settings__saved',
};

addStyles({
	[CSS.saved]: {
		color: '',
		'padding-left': DefaultTheme.space(2),
		height: DefaultTheme.space(6),
	},
});

export class ChangeSettingsComponent extends IMithrilStateComponent {
	@observable changingSettings = false;

	dateFormat: SelectInputState = new SelectInputState(T.SETTINGS_DATE_FORMAT);
	units: SelectInputState = new SelectInputState(T.SETTINGS_UNITS);
	form = new IkeInputStateGroup();

	constructor() {
		super();
		makeObservable(this);

		this.form.add(this.units);
		this.form.add(this.dateFormat);

		UNIT_OPTIONS.forEach((unit) => this.units.addOption({ key: Translate(unit.label), value: unit.unit }));
		DATE_OPTIONS.forEach((dateFormat) => this.dateFormat.addOption({ key: dateFormat, value: dateFormat }));
	}

	get nodes(): MithrilVNode[] {
		return [SelectInputComponent.view(this.dateFormat), SelectInputComponent.view(this.units)];
	}

	viewActions() {
		const submitChildren = this.changingSettings ? Spinner.view() : Translate(T.SETTINGS_DONE);
		return m('button', { className: 'mdl-button', disabled: this.changingSettings, type: 'submit' }, submitChildren);
	}

	@action reset() {
		this.changingSettings = false;
		this.form.reset();
		this.units.setValue(Services.UserSettings.units);
		this.dateFormat.setValue(Services.UserSettings.dateFormat);
	}

	@action open() {
		this.reset();
		this.autorun((_) => this.updateSettings());

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		ModalService.open({
			title: T.SETTINGS_TITLE,
			content: () => this.view(),
			actions: () => this.viewActions(),
			onSubmit: () => this.close(),
		});
	}
	@action setDone(e?: Error) {
		this.changingSettings = false;
		this.reset();

		if (e) {
			NotificationService.error({ key: T.SETTINGS_ERROR_SAVING });
		} else {
			NotificationService.success({ key: T.SETTINGS_SAVED });
		}
	}

	@action watchProgress(promise: Promise<any>) {
		this.form.setDisabled(true);
		this.changingSettings = true;

		promise.then((_) => this.setDone()).catch((e) => this.setDone(e));
	}

	updateSettings() {
		var currentUnits = this.units.value;
		var currentDateFormat = this.dateFormat.value;

		if (currentUnits !== Services.UserSettings.units) {
			this.watchProgress(Services.UserSettings.setUnits(currentUnits));
			return true;
		}

		if (currentDateFormat !== Services.UserSettings.dateFormat) {
			this.watchProgress(Services.UserSettings.setDateFormat(currentDateFormat));
			return true;
		}
		return false;
	}

	@action close() {
		if (ModalService.isOpen) {
			ModalService.close(false);
		}
		this.reset();
	}
}
